/**
 * PDP-001 R6F — CANONICAL CAMPAIGN RENDERING AUTHORITY.
 *
 * Every patient-visible campaign surface (provider-side preview,
 * patient profile multi-campaign panel, search-card commercial
 * teaser) reads its visual structure from THIS module.  Provider
 * preview MUST equal patient rendering at the pixel/structure level
 * — they share one component path now.
 *
 * Two surfaces are exported:
 *
 *   • CampaignCardFull — the rich card.  Used by:
 *       - ActiveCampaignsPanel (patient public profile)
 *       - Provider-side step-4 patient-preview in
 *         ClinicCampaignsDashboard
 *
 *   • CampaignTeaser — the compact, commercially-loud teaser that
 *     replaces the bare chip on a search card whenever a clinic has
 *     at least one active campaign.  Used by:
 *       - ClinicCard (search grid)
 *       - ClinicListItem (search list)
 *
 * Data contract (single shape across surfaces):
 *
 *   campaign: {
 *     campaign_id, treatment_type, title, short_badge_text,
 *     description?, campaign_items?, discount_type, discount_value,
 *     campaign_price_hint?, original_price_hint?, currency,
 *     start_at_utc?, end_at_utc?
 *   }
 *
 * A `form` shape from the provider editor (where `campaign_items`
 * carries `{label, included, note}` rows already) maps 1:1 — the
 * provider-side wrapper simply forwards `form` as `campaign`.
 */

import React from 'react';
import { useTranslation } from 'react-i18next';
import { formatDateLocale } from '../../utils/dateFormat';
import { Badge } from '../ui/badge';
import { Button } from '../ui/button';
import { Tag, Calendar, CheckCircle2, MessageCircle, Sparkles, Clock, ChevronRight } from 'lucide-react';

// ─────────────────────────────────────────────────────────────────────
// Shared helpers — single source of truth for label / price / date
// formatting across every campaign surface.
// ─────────────────────────────────────────────────────────────────────

export function prettifySlug(s) {
  if (!s) return '';
  return String(s)
    .split(/[-_]/)
    .filter(Boolean)
    .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
    .join(' ');
}

export function fmtDate(iso) {
  if (!iso) return '';
  try {
    return formatDateLocale(iso);
  } catch (_) {
    return '';
  }
}

/** Days remaining until end_at_utc — null if unbounded. */
function daysLeft(iso) {
  if (!iso) return null;
  try {
    const ms = new Date(iso).getTime() - Date.now();
    if (!Number.isFinite(ms)) return null;
    const days = Math.ceil(ms / 86_400_000);
    return days > 0 ? days : 0;
  } catch (_) {
    return null;
  }
}

function fmtMoney(amount, currency = 'EUR') {
  if (amount == null || !Number.isFinite(Number(amount))) return null;
  try {
    return `${currency} ${Number(amount).toLocaleString()}`;
  } catch (_) {
    return `${currency} ${amount}`;
  }
}

/** Discount summary as a React node.  Single source for every
 *  surface — covers `percentage`, `fixed`, and `package` types. */
export function DiscountSummary({ campaign, testid }) {
  const c = campaign || {};
  if (c.discount_type === 'percentage' && Number(c.discount_value) > 0) {
    return (
      <span className="text-emerald-800 font-semibold" data-testid={testid}>
        {c.discount_value}% off
      </span>
    );
  }
  if (
    (c.discount_type === 'fixed' || c.discount_type === 'package') &&
    c.campaign_price_hint != null
  ) {
    const cur = c.currency || 'EUR';
    const orig = fmtMoney(c.original_price_hint, cur);
    const now = fmtMoney(c.campaign_price_hint, cur);
    return (
      <span data-testid={testid}>
        {orig && (
          <span className="line-through text-emerald-700/60 mr-1.5">{orig}</span>
        )}
        {now && <span className="font-semibold text-emerald-900">{now}</span>}
      </span>
    );
  }
  return null;
}

/** Strike-through old + bold new price.  Used by the search teaser
 *  even when discount_type=percentage AND both hints are present —
 *  the visual "X EUR → Y EUR" cue is the strongest commercial
 *  affordance.  Returns null when no price information is available. */
function PriceStrip({ campaign, testid }) {
  const c = campaign || {};
  const cur = c.currency || 'EUR';
  const orig = fmtMoney(c.original_price_hint, cur);
  const now = fmtMoney(c.campaign_price_hint, cur);
  if (!orig && !now) return null;
  return (
    <span className="inline-flex items-baseline gap-1.5" data-testid={testid}>
      {orig && (
        <span className="text-xs line-through text-slate-400">{orig}</span>
      )}
      {now && (
        <span className="text-sm font-bold text-emerald-700">{now}</span>
      )}
    </span>
  );
}

// ─────────────────────────────────────────────────────────────────────
// FULL CARD — used by the patient profile AND the provider preview.
// One component, one shape, by construction parity.
// ─────────────────────────────────────────────────────────────────────

export function CampaignCardFull({
  campaign,
  index = 0,
  treatmentLabel,
  // CTA wiring is optional — provider preview passes nothing
  // (action surface is the "Save campaign" button outside),
  // while ActiveCampaignsPanel passes `onRequest` so the patient
  // can request a quote.
  onRequest,
  ctaLabel = null,   // defaults to t('internationalOffer.requestCta') below
  ctaIcon = <MessageCircle className="h-3.5 w-3.5" />,
  // When `true` the validity / "ends in N days" pill is hidden — used
  // by the provider preview where end_at_utc is still being chosen.
  hideValidity = false,
  testidPrefix = 'campaign-card',
  className = '',
}) {
  // STAGE-3G-TERMINOLOGY-01 — these patient-facing strings were hardcoded in
  // English with no TR counterpart at all. They now resolve through the
  // canonical i18n framework under the `internationalOffer.*` namespace, kept
  // separate from `offer.*`, which is the provider QUOTATION vocabulary.
  const { t } = useTranslation();
  const c = campaign || {};
  const tName = treatmentLabel || (c.treatment_type ? prettifySlug(c.treatment_type) : '');
  const itemsRendered = (c.campaign_items || []).filter(
    (it) => (it.label || '').trim(),
  );
  const dLeft = !hideValidity ? daysLeft(c.end_at_utc) : null;

  return (
    <article
      className={`relative rounded-xl border-2 border-emerald-300/70 bg-gradient-to-br from-emerald-50/80 to-white p-5 space-y-3 flex flex-col shadow-sm ${className}`}
      data-testid={`${testidPrefix}-${index}`}
      data-campaign-id={c.campaign_id}
      data-render-generation="PDP002-R2"
      data-render-authority="CampaignCardFull-v2"
      data-sprint-fingerprint="COM-FOUNDATION"
    >
      {/* Diagonal International Offer stripe — purely visual commercial cue */}
      <div className="absolute top-0 right-0 -mt-px -mr-px overflow-hidden rounded-tr-xl pointer-events-none">
        <div className="bg-emerald-500 text-white text-[9px] font-bold tracking-wider px-3 py-0.5 transform rotate-0 origin-top-right shadow-sm">
          {t('internationalOffer.stripe')}
        </div>
      </div>

      <header className="flex flex-wrap items-center gap-2 pr-12">
        {c.short_badge_text ? (
          <Badge
            className="bg-emerald-600 hover:bg-emerald-600 text-white text-[10px] font-semibold tracking-wide"
            data-testid={`${testidPrefix}-badge-${index}`}
          >
            <Sparkles className="h-2.5 w-2.5 mr-1" />
            {c.short_badge_text}
          </Badge>
        ) : null}
        <span
          className="text-base font-bold text-emerald-950 leading-snug"
          data-testid={`${testidPrefix}-title-${index}`}
        >
          {c.title || '(untitled)'}
        </span>
      </header>

      {tName && (
        <p
          className="text-[11px] text-emerald-900/70"
          data-testid={`${testidPrefix}-treatment-${index}`}
        >
          For:{' '}
          <strong className="font-semibold text-emerald-900">{tName}</strong>
        </p>
      )}

      {c.description && (
        <p
          className="text-xs text-emerald-900/85 leading-relaxed line-clamp-3"
          data-testid={`${testidPrefix}-description-${index}`}
        >
          {c.description}
        </p>
      )}

      <div className="flex flex-wrap items-baseline gap-x-3 gap-y-1 text-sm">
        <DiscountSummary
          campaign={c}
          testid={`${testidPrefix}-discount-${index}`}
        />
        {!hideValidity && c.end_at_utc && (
          <span
            className="inline-flex items-center gap-1 text-[11px] text-emerald-900/70"
            data-testid={`${testidPrefix}-end-${index}`}
          >
            <Calendar className="h-3 w-3" />
            Valid until {fmtDate(c.end_at_utc)}
            {dLeft != null && dLeft <= 14 && (
              <span className="ml-1 text-amber-700 font-semibold">
                · ends in {dLeft}d
              </span>
            )}
          </span>
        )}
      </div>

      {itemsRendered.length > 0 && (
        <div className="pt-1" data-testid={`${testidPrefix}-items-${index}`}>
          <p className="text-[10px] uppercase tracking-wider text-emerald-700/70 mb-1.5 font-semibold">
            {t('internationalOffer.included')}
          </p>
          <ul className="space-y-1">
            {itemsRendered.map((it, idx) => (
              <li
                key={idx}
                className={`text-xs flex items-start gap-1.5 ${
                  it.included === false
                    ? 'text-emerald-900/40 line-through'
                    : 'text-emerald-900/85'
                }`}
                data-testid={`${testidPrefix}-${index}-item-${idx}`}
              >
                <CheckCircle2
                  className={`h-3.5 w-3.5 mt-0.5 flex-shrink-0 ${
                    it.included === false
                      ? 'text-slate-300'
                      : 'text-emerald-500'
                  }`}
                />
                <span>
                  {it.label}
                  {it.note ? (
                    <span className="text-emerald-900/60"> — {it.note}</span>
                  ) : null}
                </span>
              </li>
            ))}
          </ul>
        </div>
      )}

      <div className="flex-1" />

      {onRequest && (
        <Button
          className="bg-emerald-600 hover:bg-emerald-700 text-white text-xs h-10 gap-1.5 mt-2 font-semibold"
          onClick={() => onRequest(c)}
          data-testid={`${testidPrefix}-cta-${index}`}
        >
          {ctaIcon}
          {ctaLabel || t('internationalOffer.requestCta')}
        </Button>
      )}
    </article>
  );
}

// ─────────────────────────────────────────────────────────────────────
// COMMERCIAL TEASER — rich mini commercial card used inside search.
// PDP-002: the previous compact strip was visually subordinate to the
// clinic header.  The new teaser owns its own vertical real estate,
// carries a real campaign-side narrative (badge + title + 1-line
// description + price + discount + included-items count + CTA), and
// behaves like a true marketplace offer.  Drives the same `+N more`
// affordance via the parent `CampaignTeaserStack`.
// ─────────────────────────────────────────────────────────────────────

export function CampaignTeaser({
  campaign,
  onOpen,
  testid = 'campaign-teaser',
  // Compact mode trims to a single line for dense list views.
  compact = false,
}) {
  const { t } = useTranslation();
  const c = campaign;
  if (!c || !c.campaign_id) return null;
  const dLeft = daysLeft(c.end_at_utc);
  const hasPrice =
    c.original_price_hint != null || c.campaign_price_hint != null;
  const showDiscountPill =
    c.discount_type === 'percentage' && Number(c.discount_value) > 0;
  const itemsCount = (c.campaign_items || []).filter((it) => (it.label || '').trim() && it.included !== false).length;
  const treatmentName = c.treatment_type ? prettifySlug(c.treatment_type) : '';

  return (
    <button
      type="button"
      onClick={(e) => {
        e.stopPropagation();
        if (onOpen) onOpen(c);
      }}
      className="group w-full text-left rounded-xl border-2 border-emerald-300/80 bg-gradient-to-br from-emerald-50 via-emerald-50/80 to-white px-3.5 py-3 hover:border-emerald-500 hover:shadow-md transition-all flex flex-col gap-2 relative overflow-hidden"
      data-testid={testid}
      data-campaign-id={c.campaign_id}
    >
      {/* International Offer stripe — same visual cue as the full card */}
      <div className="absolute top-0 right-0 overflow-hidden rounded-tr-xl pointer-events-none">
        <div className="bg-emerald-500 text-white text-[8px] font-bold tracking-wider px-2 py-0.5 shadow-sm">
          {t('internationalOffer.stripe')}
        </div>
      </div>

      {/* Title row */}
      <div className="flex items-start gap-2 pr-12">
        <div className="w-7 h-7 rounded-md bg-emerald-600 text-white flex items-center justify-center shadow-sm flex-shrink-0">
          <Sparkles className="h-3.5 w-3.5" />
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-1.5 flex-wrap">
            {c.short_badge_text && (
              <Badge
                className="bg-emerald-600 hover:bg-emerald-600 text-white text-[9px] font-semibold tracking-wide py-0 h-4"
                data-testid={`${testid}-badge`}
              >
                {c.short_badge_text}
              </Badge>
            )}
            {treatmentName && (
              <span className="text-[10px] uppercase tracking-wider text-emerald-700/70 font-semibold">
                {treatmentName}
              </span>
            )}
          </div>
          <p
            className="text-[13px] font-bold text-emerald-950 leading-snug mt-0.5 line-clamp-2"
            data-testid={`${testid}-title`}
          >
            {c.title || t('internationalOffer.untitled')}
          </p>
        </div>
      </div>

      {/* Description — 1 line in compact, 2 lines otherwise */}
      {c.description && (
        <p
          className={`text-[11px] text-emerald-900/75 leading-relaxed ${compact ? 'line-clamp-1' : 'line-clamp-2'}`}
          data-testid={`${testid}-description`}
        >
          {c.description}
        </p>
      )}

      {/* Price + discount row — prominent commercial cue */}
      {(hasPrice || showDiscountPill) && (
        <div className="flex items-baseline gap-2 flex-wrap">
          {hasPrice && (
            <span className="inline-flex items-baseline gap-1.5" data-testid={`${testid}-price`}>
              {c.original_price_hint != null && (
                <span className="text-xs line-through text-slate-400">
                  {fmtMoney(c.original_price_hint, c.currency || 'EUR')}
                </span>
              )}
              {c.campaign_price_hint != null && (
                <span className="text-base font-bold text-emerald-700">
                  {fmtMoney(c.campaign_price_hint, c.currency || 'EUR')}
                </span>
              )}
            </span>
          )}
          {showDiscountPill && (
            <span
              className="text-[10px] font-bold text-emerald-50 bg-emerald-600 px-1.5 py-0.5 rounded shadow-sm"
              data-testid={`${testid}-discount`}
            >
              SAVE {c.discount_value}%
            </span>
          )}
        </div>
      )}

      {/* Meta row */}
      <div className="flex items-center flex-wrap gap-x-2.5 gap-y-0.5 text-[10px] text-slate-600">
        {itemsCount > 0 && (
          <span className="inline-flex items-center gap-0.5 text-emerald-800" data-testid={`${testid}-items-count`}>
            <CheckCircle2 className="h-3 w-3" />
            {itemsCount} item{itemsCount !== 1 ? 's' : ''} included
          </span>
        )}
        {dLeft != null && dLeft <= 30 && (
          <span
            className="inline-flex items-center gap-1 text-amber-700 font-semibold"
            data-testid={`${testid}-ends-in`}
          >
            <Clock className="h-3 w-3" />
            Ends in {dLeft}d
          </span>
        )}
      </div>

      {/* CTA row */}
      <div className="flex items-center justify-end gap-1 text-[11px] font-semibold text-emerald-700 group-hover:text-emerald-900 pt-0.5">
        {t('internationalOffer.view')}
        <ChevronRight className="h-3.5 w-3.5" />
      </div>
    </button>
  );
}

// ─────────────────────────────────────────────────────────────────────
// MULTI-CAMPAIGN STACK — renders up to 2 rich teasers stacked vertically,
// with an inline "View N more offers" footer when more than 2 exist.
// Replaces the previous "+N more offer" pill that gave the patient no
// signal about what the other offers actually contained.
// ─────────────────────────────────────────────────────────────────────

export function CampaignTeaserStack({
  campaigns,
  onOpen,
  testid = 'campaign-teaser-stack',
  // How many teasers to render before collapsing the remainder
  // into a "more offers" pill.  Defaults to 2 (lead + secondary).
  maxVisible = 2,
}) {
  const { t } = useTranslation();
  const list = (campaigns || []).filter((c) => c && c.campaign_id);
  if (list.length === 0) return null;
  const visible = list.slice(0, maxVisible);
  const remaining = list.length - visible.length;
  return (
    <div className="space-y-2" data-testid={testid} data-total-count={list.length} data-visible-count={visible.length} data-remaining-count={remaining} data-render-generation="PDP002-R2" data-render-authority="CampaignTeaserStack-v2" data-sprint-fingerprint="COM-FOUNDATION">
      {visible.map((c, i) => (
        <CampaignTeaser
          key={c.campaign_id}
          campaign={c}
          onOpen={() => onOpen && onOpen(c)}
          testid={`${testid}-${i}`}
          compact={visible.length > 1}
        />
      ))}
      {remaining > 0 && (
        <button
          type="button"
          onClick={(e) => {
            e.stopPropagation();
            if (onOpen) onOpen(list[maxVisible]);
          }}
          className="w-full text-center py-1.5 text-[11px] font-semibold text-emerald-700 hover:text-emerald-900 hover:bg-emerald-50 rounded-md transition-colors inline-flex items-center justify-center gap-1"
          data-testid={`${testid}-more`}
        >
          {t('internationalOffer.more', { count: remaining })}
          <ChevronRight className="h-3 w-3" />
        </button>
      )}
    </div>
  );
}

// Backwards-compat helper — single campaign (lead-only) shorthand
// consumed by older surfaces (kept exported, never gives "+N more").
export function CampaignTeaserSingle({ campaign, onOpen, testid }) {
  return <CampaignTeaser campaign={campaign} onOpen={onOpen} testid={testid} />;
}

export default CampaignCardFull;
